import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "sonner";
import {
  CheckCircle2,
  X,
  Star,
  Shield,
  Bell,
  ClipboardList,
  Users,
  TrendingUp,
  FileText,
  Headphones,
  Zap,
  Heart,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Crown,
  Loader2,
  Sparkles,
} from "lucide-react";

/* ── Stripe price IDs ── */
const PLANS = [
  {
    id: "monthly",
    label: "Monthly",
    priceId: "price_1T2s4fI70UY7BgnzxLgmzieC",
    perMonth: 12.99,
    total: null,
    billingNote: "Billed monthly. Cancel anytime.",
    savingPct: 0,
    badge: null,
    highlight: false,
  },
  {
    id: "3month",
    label: "3 Months",
    priceId: "price_1T2s5FI70UY7Bgnz7igO2eMK",
    perMonth: 10.99,
    total: 32.97,
    billingNote: "Billed as €32.97 every 3 months.",
    savingPct: 15,
    badge: null,
    highlight: false,
  },
  {
    id: "6month",
    label: "6 Months",
    priceId: "price_1T2s5cI70UY7BgnzRm1Vf1XS",
    perMonth: 9.49,
    total: 56.94,
    billingNote: "Billed as €56.94 every 6 months.",
    savingPct: 27,
    badge: "Most popular",
    highlight: true,
  },
  {
    id: "annual",
    label: "Annual",
    priceId: "price_1T2s5zI70UY7Bgnzdb7QM6ya",
    perMonth: 7.99,
    total: 95.88,
    billingNote: "Billed as €95.88/year.",
    savingPct: 38,
    badge: "Best value",
    highlight: false,
  },
] as const;

/* ── Data ── */
const FREE_VS_PREMIUM: {
  category: string;
  icon: React.ElementType;
  rows: { feature: string; free: boolean | string; premium: boolean | string }[];
}[] = [
  {
    category: "Search & Discovery",
    icon: Zap,
    rows: [
      { feature: "Browse all nursing homes", free: true, premium: true },
      { feature: "Filter by county, category & Fair Deal", free: true, premium: true },
      { feature: "Distance / 'Near me' filter", free: true, premium: true },
      { feature: "Side-by-side home comparison", free: "Up to 2", premium: "Up to 4" },
      { feature: "Advanced search with availability filter", free: false, premium: true },
    ],
  },
  {
    category: "Enquiry Management",
    icon: ClipboardList,
    rows: [
      { feature: "Save homes to shortlist", free: "Up to 3", premium: "Unlimited" },
      { feature: "Enquiry tracker with status pipeline", free: "Basic", premium: "Full pipeline + reminders" },
      { feature: "Notes on each enquiry", free: false, premium: true },
      { feature: "Follow-up date reminders", free: false, premium: true },
      { feature: "Managed enquiry service (we contact on your behalf)", free: false, premium: true },
    ],
  },
  {
    category: "Information & Insights",
    icon: TrendingUp,
    rows: [
      { feature: "HIQA inspection reports", free: true, premium: true },
      { feature: "Fair Deal cost estimates", free: "Basic calculator", premium: "Personalised modelling" },
      { feature: "Weekly digest of new homes in your area", free: false, premium: true },
      { feature: "Vacancy alerts for shortlisted homes", free: false, premium: true },
      { feature: "Price range & bed count data", free: true, premium: true },
    ],
  },
  {
    category: "Guides & Expert Access",
    icon: FileText,
    rows: [
      { feature: "Free guides & resources", free: true, premium: true },
      { feature: "Downloadable county guides", free: true, premium: true },
      { feature: "Consultation booking", free: true, premium: "Priority booking" },
      { feature: "Dedicated care advisor", free: false, premium: true },
    ],
  },
  {
    category: "Support",
    icon: Headphones,
    rows: [
      { feature: "Email support", free: true, premium: true },
      { feature: "Priority response time", free: false, premium: "< 4 hours" },
      { feature: "Onboarding call", free: false, premium: true },
    ],
  },
];

const PREMIUM_FEATURES = [
  {
    icon: Heart,
    title: "Managed Enquiries",
    tagline: "We do the legwork",
    description:
      "Tell us your requirements once. Our care advisors contact shortlisted homes on your behalf, gather waiting list and pricing info, and report back weekly — so you don't have to make dozens of calls.",
  },
  {
    icon: ClipboardList,
    title: "Full Enquiry Tracker",
    tagline: "Never lose track",
    description:
      "A full-pipeline tracker (Interested → Applied → Waitlisted) for every home, with inline notes, follow-up date reminders, and overdue alerts. See everything at a glance.",
  },
  {
    icon: Bell,
    title: "Weekly Updates",
    tagline: "Stay ahead of waiting lists",
    description:
      "A curated weekly digest lands in your inbox: new homes matching your criteria, vacancy signals in your preferred counties, and any HIQA inspection changes for shortlisted homes.",
  },
  {
    icon: TrendingUp,
    title: "Personalised Fair Deal Modelling",
    tagline: "Know the real cost",
    description:
      "Go beyond the basic calculator. Input your financial situation and get a tailored breakdown of Fair Deal contributions, top-up costs, and what to expect across your shortlisted homes.",
  },
  {
    icon: Users,
    title: "Collaborative Shortlist",
    tagline: "Decide together",
    description:
      "Share your shortlist with family members. Each person can add notes and rate homes. Reach a decision together, even when you're not in the same room.",
  },
  {
    icon: Shield,
    title: "Priority Expert Access",
    tagline: "Expert guidance, faster",
    description:
      "Priority booking slots with our network of care advisors and nursing home specialists. Skip the queue when you need urgent guidance.",
  },
];

const FAQS = [
  {
    q: "Can I cancel at any time?",
    a: "Yes. You can cancel your subscription at any time from your account settings. You'll retain access until the end of your billing period and won't be charged again.",
  },
  {
    q: "What does 'Managed Enquiries' actually mean?",
    a: "Our care advisors will contact your shortlisted nursing homes on your behalf — asking about availability, waiting lists, pricing, and any specific care requirements you've told us about. You receive a written summary each week.",
  },
  {
    q: "Is my personal information shared with nursing homes?",
    a: "Only with homes you've explicitly shortlisted for the managed service. We'll always ask your permission before making contact on your behalf.",
  },
  {
    q: "Can I upgrade after signing up for Free?",
    a: "Absolutely. You can upgrade to Premium at any point from your dashboard. Your existing shortlist and enquiry data is preserved.",
  },
  {
    q: "Do I need a subscription to browse nursing homes?",
    a: "No. All nursing home listings, HIQA data, and county guides are free to browse. Premium unlocks the managed service, advanced tracking, and personalised insights.",
  },
];

/* ── Sub-components ── */
function CheckCell({ value }: { value: boolean | string }) {
  if (value === false) {
    return <X className="mx-auto h-4 w-4 text-muted-foreground/40" />;
  }
  if (value === true) {
    return <CheckCircle2 className="mx-auto h-4 w-4 text-primary" />;
  }
  return (
    <span className="text-xs font-medium text-foreground text-center block leading-tight">
      {value}
    </span>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border last:border-0">
      <button
        className="w-full flex items-center justify-between gap-4 py-4 text-left text-sm font-medium hover:text-primary transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        {q}
        {open ? (
          <ChevronUp size={16} className="shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown size={16} className="shrink-0 text-muted-foreground" />
        )}
      </button>
      {open && (
        <p className="pb-4 text-sm text-muted-foreground leading-relaxed">{a}</p>
      )}
    </div>
  );
}

/* ── Page ── */
export default function PremiumPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<typeof PLANS[number]["id"]>("6month");
  const [loading, setLoading] = useState(false);

  const activePlan = PLANS.find((p) => p.id === selectedPlan)!;

  const handleCheckout = async () => {
    if (!user) {
      navigate(`/auth/signup?redirectTo=/premium`);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId: activePlan.priceId },
      });
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      toast.error("Couldn't start checkout", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Grandpal Premium | Find the right care home faster</title>
        <meta name="description" content="Grandpal Premium handles the research, enquiries, and weekly follow-ups so you can focus on what matters — your family." />
      </Helmet>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-primary pt-20 pb-28 text-primary-foreground">
        <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-primary-foreground/5" />
        <div className="pointer-events-none absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-primary-foreground/5" />

        <div className="container relative text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-4 bg-primary-foreground/15 text-primary-foreground border-primary-foreground/30 text-xs font-semibold tracking-wide uppercase px-3 py-1">
              <Crown size={11} className="mr-1.5" />
              Grandpal Premium
            </Badge>

            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-5 max-w-3xl mx-auto">
              Finding the right care home
              <br className="hidden md:block" />
              {" "}shouldn't be a full-time job
            </h1>

            <p className="text-primary-foreground/80 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10">
              Grandpal Premium handles the research, enquiries, and weekly follow-ups
              so you can focus on what matters — your family.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-semibold px-8 gap-2"
                onClick={handleCheckout}
                disabled={loading}
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Crown size={16} />}
                Start free trial
                {!loading && <ArrowRight size={16} />}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-primary-foreground/40 text-primary-foreground bg-transparent hover:bg-primary-foreground/10"
                onClick={() => navigate("/dashboard")}
              >
                Explore for free
              </Button>
            </div>
            <p className="text-xs text-primary-foreground/50 mt-4">
              14-day free trial · No credit card required · Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Social proof strip ── */}
      <section className="bg-secondary/40 border-y border-border py-5">
        <div className="container flex flex-wrap items-center justify-center gap-8 text-sm text-muted-foreground">
          {[
            { icon: Star, text: "4.9 / 5 from 200+ families" },
            { icon: Shield, text: "Your data is never sold" },
            { icon: Users, text: "800+ Irish nursing homes covered" },
          ].map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-2 font-medium">
              <Icon size={15} className="text-primary" />
              {text}
            </div>
          ))}
        </div>
      </section>

      {/* ── Feature highlight cards ── */}
      <section className="py-20 bg-background">
        <div className="container">
          <div className="text-center mb-14">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-3">
              Everything in Premium
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Six features designed for families navigating Ireland's nursing home system.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PREMIUM_FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="bg-card rounded-xl border border-border p-6 hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <f.icon size={18} className="text-primary" />
                </div>
                <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-1">
                  {f.tagline}
                </p>
                <h3 className="font-semibold text-foreground text-lg mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {f.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Free vs Premium comparison table ── */}
      <section className="py-20 bg-muted/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-3">
              Free vs Premium
            </h2>
            <p className="text-muted-foreground">A complete breakdown of what's included at each tier.</p>
          </div>

          <div className="max-w-3xl mx-auto rounded-xl border border-border bg-card overflow-hidden shadow-sm">
            <div className="grid grid-cols-[1fr_100px_140px] bg-muted/50 border-b border-border text-sm font-semibold">
              <div className="px-5 py-3 text-muted-foreground">Feature</div>
              <div className="px-3 py-3 text-center text-muted-foreground">Free</div>
              <div className="px-3 py-3 text-center">
                <span className="inline-flex items-center gap-1 text-primary">
                  <Crown size={12} />
                  Premium
                </span>
              </div>
            </div>

            {FREE_VS_PREMIUM.map((section, si) => (
              <div key={section.category}>
                <div className="grid grid-cols-[1fr_100px_140px] bg-accent/40 border-y border-border/50">
                  <div className="px-5 py-2.5 flex items-center gap-2">
                    <section.icon size={13} className="text-primary" />
                    <span className="text-xs font-semibold text-foreground/70 uppercase tracking-wide">
                      {section.category}
                    </span>
                  </div>
                  <div />
                  <div className="bg-primary/5" />
                </div>

                {section.rows.map((row, ri) => (
                  <div
                    key={row.feature}
                    className={`grid grid-cols-[1fr_100px_140px] text-sm ${
                      ri < section.rows.length - 1 ? "border-b border-border/40" : ""
                    }`}
                  >
                    <div className="px-5 py-3 text-foreground">{row.feature}</div>
                    <div className="px-3 py-3 flex items-center justify-center">
                      <CheckCell value={row.free} />
                    </div>
                    <div className="px-3 py-3 flex items-center justify-center bg-primary/5">
                      <CheckCell value={row.premium} />
                    </div>
                  </div>
                ))}

                {si < FREE_VS_PREMIUM.length - 1 && (
                  <Separator className="bg-border/30" />
                )}
              </div>
            ))}

            <div className="grid grid-cols-[1fr_100px_140px] bg-muted/30 border-t border-border">
              <div className="px-5 py-4" />
              <div className="px-3 py-4 flex items-center justify-center">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-8"
                  onClick={() => navigate("/auth/signup")}
                >
                  Sign up free
                </Button>
              </div>
              <div className="px-3 py-4 flex items-center justify-center bg-primary/5">
                <Button
                  size="sm"
                  className="text-xs h-8 gap-1"
                  onClick={handleCheckout}
                  disabled={loading}
                >
                  <Crown size={11} />
                  Get Premium
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Testimonial ── */}
      <section className="py-20 bg-background">
        <div className="container max-w-3xl text-center">
          <div className="inline-flex items-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={18} className="text-primary fill-primary" />
            ))}
          </div>
          <blockquote className="font-serif text-2xl md:text-3xl font-medium text-foreground leading-snug mb-6">
            "After three weeks of phone calls and dead ends, Grandpal's advisors gave us
            a clear shortlist within days. We found the right home for Dad two weeks later."
          </blockquote>
          <p className="text-muted-foreground text-sm font-medium">
            Siobhán M. — Dublin family, Premium subscriber
          </p>
        </div>
      </section>

      {/* ── Pricing cards ── */}
      <section className="py-20 bg-muted/30">
        <div className="container">
          <div className="text-center mb-4">
            <h2 className="font-serif text-3xl font-semibold mb-3">Choose your plan</h2>
            <p className="text-muted-foreground">
              Longer plans save more — most families find their home within 3–6 months.
            </p>
          </div>

          {/* Saving callout */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold px-4 py-2 rounded-full">
              <Sparkles size={13} />
              Save up to 38% with an annual plan vs monthly
            </div>
          </div>

          {/* Plan selector grid */}
          <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {PLANS.map((plan) => {
              const isSelected = selectedPlan === plan.id;
              return (
                <motion.button
                  key={plan.id}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`relative text-left rounded-xl border-2 p-5 transition-all ${
                    plan.highlight && !isSelected
                      ? "border-primary/40 bg-primary/5"
                      : isSelected
                      ? "border-primary bg-primary text-primary-foreground shadow-lg"
                      : "border-border bg-card hover:border-primary/30"
                  }`}
                >
                  {plan.badge && (
                    <span className={`absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full ${
                      isSelected
                        ? "bg-primary-foreground text-primary"
                        : "bg-primary text-primary-foreground"
                    }`}>
                      {plan.badge}
                    </span>
                  )}

                  <p className={`text-xs font-semibold mb-3 ${isSelected ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                    {plan.label}
                  </p>

                  <div className="flex items-end gap-0.5 mb-1">
                    <span className="text-3xl font-bold">€{plan.perMonth}</span>
                    <span className={`text-sm mb-0.5 ${isSelected ? "text-primary-foreground/60" : "text-muted-foreground"}`}>/mo</span>
                  </div>

                  {plan.total && (
                    <p className={`text-xs ${isSelected ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                      €{plan.total} billed
                    </p>
                  )}
                  {!plan.total && (
                    <p className={`text-xs ${isSelected ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                      Billed monthly
                    </p>
                  )}

                  {plan.savingPct > 0 && (
                    <span className={`mt-3 inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                      isSelected
                        ? "bg-primary-foreground/20 text-primary-foreground"
                        : "bg-primary/10 text-primary"
                    }`}>
                      Save {plan.savingPct}%
                    </span>
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* CTA block */}
          <div className="max-w-md mx-auto text-center">
            <p className="text-xs text-muted-foreground mb-4">{activePlan.billingNote}</p>
            <Button
              size="lg"
              className="w-full font-semibold gap-2"
              onClick={handleCheckout}
              disabled={loading}
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Crown size={16} />
              )}
              {loading ? "Opening checkout…" : `Start free trial — €${activePlan.perMonth}/mo`}
              {!loading && <ArrowRight size={16} />}
            </Button>
            <p className="text-xs text-muted-foreground mt-3">
              14-day free trial · No credit card required · Cancel anytime
            </p>

            {/* Free plan link */}
            <div className="mt-8 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground mb-3">
                Not ready to upgrade? The free plan is always available.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/auth/signup")}
              >
                Get started free
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-background">
        <div className="container max-w-2xl">
          <h2 className="font-serif text-3xl font-semibold mb-10 text-center">
            Frequently asked questions
          </h2>
          <div className="bg-card rounded-xl border border-border px-6">
            {FAQS.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container text-center max-w-2xl">
          <Crown size={32} className="mx-auto mb-5 text-primary-foreground/60" />
          <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-4">
            Start finding the right home today
          </h2>
          <p className="text-primary-foreground/70 mb-8 text-lg">
            Join hundreds of Irish families who use Grandpal to navigate nursing home care with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-semibold px-8 gap-2"
              onClick={handleCheckout}
              disabled={loading}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              Start free trial
              {!loading && <ArrowRight size={16} />}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-primary-foreground/40 text-primary-foreground bg-transparent hover:bg-primary-foreground/10"
              onClick={() => navigate("/nursing-homes")}
            >
              Browse nursing homes
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
